import React, { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate } from "react-router-dom";
import {
  House, Receipt, Heart, ChatCircle, Gear, Star, Package, CheckCircle,
} from "@phosphor-icons/react";
import DashboardShell from "@/components/DashboardShell";
import { api } from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";

const links = [
  { to: "/dashboard/buyer", end: true, label: "Overview", Icon: House },
  { to: "/dashboard/buyer/orders", label: "Orders", Icon: Receipt },
  { to: "/dashboard/buyer/saved", label: "Saved", Icon: Heart },
  { to: "/messages", label: "Messages", Icon: ChatCircle },
];

function Overview() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)); }, []);
  const active = orders.filter((o) => !["completed", "cancelled", "refunded"].includes(o.status));
  const completed = orders.filter((o) => o.status === "completed");
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-4">
        <Stat label="Active orders" value={active.length} icon={Package} />
        <Stat label="Completed" value={completed.length} icon={CheckCircle} />
        <Stat label="Total spent" value={`$${orders.reduce((s, o) => s + (o.total || 0), 0).toFixed(0)}`} icon={Receipt} />
      </div>
      <Card title="Recent orders">
        <OrderTable orders={orders.slice(0, 6)} />
      </Card>
    </>
  );
}

function OrdersPage() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then(({ data }) => setOrders(data)); }, []);
  return <Card title="All orders"><OrderTable orders={orders} /></Card>;
}

function Saved() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/saved").then(({ data }) => setItems(data)); }, []);
  return (
    <Card title="Saved services">
      {items.length === 0 ? <div className="text-sm text-gray-500 p-4">Nothing saved yet.</div> :
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((s) => <ServiceCard key={s.id} service={s} />)}
        </div>}
    </Card>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider font-bold"><Icon weight="duotone" /> {label}</div>
      <div className="font-display font-black text-3xl text-mv-secondary mt-2">{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 font-display font-bold text-mv-secondary">{title}</div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function OrderTable({ orders }) {
  const nav = useNavigate();
  if (!orders.length) return <div className="text-gray-500 text-sm p-4">No orders yet.</div>;
  return (
    <div className="mv-table-wrap">
      <table className="w-full text-sm" data-testid="orders-table">
        <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
          <tr className="border-b border-gray-100">
            <th className="py-2">Service</th><th>Status</th><th>Total</th><th>Date</th><th></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => nav(`/dashboard/buyer/orders/${o.id}`)}>
              <td className="py-3 pr-3 font-bold text-mv-secondary">{o.service_title}</td>
              <td><span className="px-2 py-0.5 rounded-full bg-mv-primary/10 text-mv-primary text-xs font-bold capitalize">{o.status.replace(/_/g, " ")}</span></td>
              <td className="font-bold">${o.total}</td>
              <td className="text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
              <td><Link to={`/dashboard/buyer/orders/${o.id}`} className="text-mv-primary font-bold text-xs">Open →</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

import OrderDetail from "@/pages/OrderDetail";

export default function BuyerDashboard() {
  return (
    <Routes>
      <Route element={<DashboardShell links={links} title="Buyer dashboard" />}>
        <Route index element={<Overview />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="saved" element={<Saved />} />
      </Route>
    </Routes>
  );
}
